import {
  CircleNotchIcon,
  EyeIcon,
  PackageIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getProviderLogo } from '@features/orders';
import { useOrdersV2 } from '@features/orders/hooks/orders-v2';
import type {
  OrderDetail,
  OrderShippingLabel,
} from '@skupreme/api-contracts/core';
import {
  Badge,
  Button,
  Card,
  CopyButton,
  FieldGrid,
  Modal,
  ItemIdentifiers,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { carrierServices } from '@utility/constants/common';
import { DateFormat, DateUtility } from '@utility/date';
import { formatAddress } from '../../lib/address';
import { formatPackageSize, formatPackageWeight } from '../../lib/packages';
import { catalogItemId, shipmentTitle } from '../../lib/shipments';
import {
  ShipmentFact,
  ShipmentItems,
  type ShipmentContent,
} from './ShipmentParts';

export type TrackingStatus = {
  tracking_id?: string;
  eta?: string;
};

export type ShipmentSummaryOrder = Pick<
  OrderDetail,
  'eta' | 'latest_delivery_date' | 'order_contents' | 'shipping_details'
>;

export type ShipmentSummaryListProps = {
  order?: ShipmentSummaryOrder;
  labels: OrderShippingLabel[];
  returnLabels?: OrderShippingLabel[];
  isLoading?: boolean;
  /** A fulfillment request is still being processed on the backend. */
  isProcessing?: boolean;
  trackingStatuses?: TrackingStatus[];
  /**
   * Set when the list is one part of a longer run of shipments, so that what
   * has shipped and what is still being planned are numbered as one. It also
   * means the order has somewhere else to say it has shipped nothing yet.
   */
  numbering?: { offset: number; total: number };
  onViewLabel: (label: OrderShippingLabel) => void;
  onRefundLabel?: (label: OrderShippingLabel) => Promise<void>;
};

function shipmentPackage(
  order: ShipmentSummaryOrder | undefined,
  trackingId?: string,
) {
  if (!trackingId) {
    return undefined;
  }

  return order?.shipping_details?.find(
    shipment =>
      shipment.tracking_number === trackingId ||
      shipment.tracking_details?.some(
        tracking => tracking.tracking_number === trackingId,
      ),
  );
}

/**
 * An order can leave in several boxes, each with its own number, so a shipment
 * has to say which lines are inside it.
 */
function shipmentContents(
  order: ShipmentSummaryOrder | undefined,
  shipment: ShipmentSummaryOrder['shipping_details'][number] | undefined,
  source: OrderShippingLabel | string,
): ShipmentContent[] {
  const label = typeof source === 'string' ? undefined : source;
  const trackingId = typeof source === 'string' ? source : source.tracking_id;

  if (label?.items?.length) {
    return label.items.reduce<ShipmentContent[]>((contents, labelItem) => {
      const itemId = labelItem.bundle_item_id || labelItem.item_id;
      if (!itemId) {
        return contents;
      }

      if (contents.some(item => item.id === itemId)) {
        return contents;
      }

      const itemIds = [labelItem.bundle_item_id, labelItem.item_id].filter(
        (id): id is string => !!id,
      );
      const shipmentItem = shipment?.items?.find(item =>
        itemIds.includes(item._id),
      );
      const orderItem = order?.order_contents?.find(item =>
        itemIds.some(id => item._id === id || item.content.item_id === id),
      );
      const bundleOrderItem = labelItem.bundle_item_id
        ? order?.order_contents?.find(
            item =>
              item._id === labelItem.bundle_item_id ||
              item.content.item_id === labelItem.bundle_item_id,
          )
        : undefined;

      contents.push({
        id: itemId,
        catalogId: catalogItemId(shipmentItem) || catalogItemId(orderItem),
        title:
          shipmentItem?.p_title ||
          orderItem?.content.title ||
          orderItem?.p_title ||
          itemId,
        sku: shipmentItem?.p_sku || orderItem?.content.sku || orderItem?.p_sku,
        upc: shipmentItem?.upc || orderItem?.upc,
        image:
          shipmentItem?.p_images?.display_image ||
          orderItem?.p_images?.display_image,
        qty:
          bundleOrderItem?.content.qty ??
          labelItem.qty ??
          shipmentItem?.qty ??
          orderItem?.content.qty ??
          0,
      });
      return contents;
    }, []);
  }

  if (shipment?.items?.length) {
    return shipment.items.map(item => ({
      id: item._id,
      catalogId: catalogItemId(item),
      title: item.p_title,
      sku: item.p_sku,
      upc: item.upc,
      image: item.p_images?.display_image,
      qty: item.qty,
    }));
  }

  if (!trackingId) {
    return [];
  }

  return (order?.order_contents || [])
    .filter(item => item.content.tracking_number === trackingId)
    .map(item => ({
      id: item._id || item.content.item_id,
      catalogId: catalogItemId(item),
      title: item.content.title || item.p_title,
      sku: item.content.sku || item.p_sku,
      upc: item.upc,
      image: item.p_images?.display_image,
      qty: item.content.qty,
    }));
}

function fulfillmentProvider(
  order: ShipmentSummaryOrder | undefined,
  shipment: ShipmentSummaryOrder['shipping_details'][number] | undefined,
  providerName?: string,
  trackingId?: string,
) {
  const allProviders =
    order?.order_contents?.flatMap(
      item => item.content.fulfillment_details || [],
    ) || [];
  const namedProvider = allProviders.find(
    provider => provider.name === providerName,
  );

  if (namedProvider) {
    return namedProvider;
  }

  const relatedItems =
    order?.order_contents?.filter(
      item =>
        item.content.tracking_number === trackingId ||
        shipment?.items.some(
          shipmentItem =>
            shipmentItem._id === item._id ||
            shipmentItem._id === item.content.item_id,
        ),
    ) || [];
  const relatedProviders = relatedItems.flatMap(
    item => item.content.fulfillment_details || [],
  );
  const providerById = relatedProviders.find(provider =>
    relatedItems.some(
      item => item.content.fulfillment_provider_id === provider._id,
    ),
  );

  if (providerById) {
    return providerById;
  }

  const uniqueProviders = [
    ...new Map(
      relatedProviders.map(provider => [provider._id, provider]),
    ).values(),
  ];

  return uniqueProviders.length === 1 ? uniqueProviders[0] : undefined;
}

type ExternalShipment = {
  trackingNumber: string;
  trackingLink?: string;
  carrier?: string;
  providerName?: string;
  providerAddress?: string;
  providerLogo?: string;
  eta?: string;
  /** When the warehouse reported it shipped. */
  shippedAt?: string;
  contents: ShipmentContent[];
};

/**
 * Warehouses on their own carrier contract report a tracking number without
 * ever buying a label from us. Those shipments have no label to open or refund,
 * but leaving them out makes the order look like it lost a package.
 */
function externalShipments(
  order: ShipmentSummaryOrder | undefined,
  labels: OrderShippingLabel[],
): ExternalShipment[] {
  const labelled = new Set(
    labels.map(label => label.tracking_id).filter(Boolean),
  );
  const shipments = new Map<string, ExternalShipment>();

  const isCovered = (trackingNumber?: string | null) =>
    !trackingNumber ||
    trackingNumber === 'TBD' ||
    labelled.has(trackingNumber) ||
    shipments.has(trackingNumber);

  (order?.shipping_details || []).forEach(shipment => {
    const trackingNumber = shipment.tracking_number;
    const hasLabelledTracking = shipment.tracking_details?.some(tracking =>
      labelled.has(tracking.tracking_number),
    );

    if (hasLabelledTracking && trackingNumber) {
      labelled.add(trackingNumber);
    }

    if (isCovered(trackingNumber) || hasLabelledTracking || !trackingNumber) {
      return;
    }

    const provider = fulfillmentProvider(
      order,
      shipment,
      undefined,
      trackingNumber,
    );

    shipments.set(trackingNumber, {
      trackingNumber,
      trackingLink: shipment.tracking_link,
      carrier: shipment.shipping_carrier,
      providerName: provider?.name,
      providerAddress: formatAddress(provider?.address),
      providerLogo: getProviderLogo(provider?.integration_type),
      eta: shipment.eta,
      shippedAt: shipment.shipped_time,
      contents: shipmentContents(order, shipment, trackingNumber),
    });
  });

  (order?.order_contents || []).forEach(item => {
    const trackingNumber = item.content.tracking_number;

    if (isCovered(trackingNumber) || !trackingNumber) {
      return;
    }

    const provider = item.content.fulfillment_details?.[0];

    shipments.set(trackingNumber, {
      trackingNumber,
      trackingLink: item.content.tracking_link,
      carrier: item.content.shipping_carrier,
      providerName: provider?.name,
      providerAddress: formatAddress(provider?.address),
      providerLogo: getProviderLogo(provider?.integration_type),
      eta: item.content.eta,
      shippedAt: item.content.shipped_time,
      contents: shipmentContents(order, undefined, trackingNumber),
    });
  });

  return [...shipments.values()];
}

function TrackingRow({
  trackingNumber,
  trackingLink,
}: {
  trackingNumber?: string;
  trackingLink?: string;
}) {
  return (
    <div className="sk:flex sk:h-7.5 sk:min-w-0 sk:items-center sk:justify-center sk:gap-2 sk:rounded-option sk:inset-shadow-hairline sk:bg-surface sk:px-3">
      {trackingLink && trackingNumber ? (
        <a
          href={trackingLink}
          target="_blank"
          rel="noreferrer"
          className="sk:min-w-0 sk:truncate sk:text-dense sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
        >
          Tracking # {trackingNumber}
        </a>
      ) : (
        <span className="sk:min-w-0 sk:truncate sk:text-dense sk:font-medium sk:text-fg-secondary">
          {trackingNumber
            ? `Tracking # ${trackingNumber}`
            : 'Tracking unavailable'}
        </span>
      )}
      {trackingNumber ? (
        <CopyButton
          value={trackingNumber}
          label="Copy tracking number"
          className="sk:shrink-0"
        />
      ) : null}
    </div>
  );
}

function ShipmentSummary({
  label,
  index,
  count,
  order,
  trackingStatuses,
  onViewLabel,
  onRefundLabel,
  isReturn,
}: {
  label: OrderShippingLabel;
  index: number;
  count: number;
  order?: ShipmentSummaryOrder;
  trackingStatuses: TrackingStatus[];
  onViewLabel: (label: OrderShippingLabel) => void;
  onRefundLabel?: (label: OrderShippingLabel) => Promise<void>;
  isReturn?: boolean;
}) {
  const [isConfirmingRefund, setIsConfirmingRefund] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const shipment = shipmentPackage(order, label.tracking_id);
  const provider = fulfillmentProvider(
    order,
    shipment,
    label.shipFrom?.name,
    label.tracking_id,
  );
  const contents = shipmentContents(order, shipment, label);
  const dimensions = formatPackageSize(label);
  const weight = formatPackageWeight(label);
  const eta =
    trackingStatuses.find(
      tracking => tracking.tracking_id === label.tracking_id,
    )?.eta ||
    shipment?.eta ||
    order?.eta ||
    order?.latest_delivery_date;
  const trackingLink = shipment?.tracking_link;
  const carrierService = label.provider
    ? carrierServices[label.provider.toLowerCase()]
    : undefined;
  const carrierName =
    carrierService?.text ||
    label.provider?.toUpperCase() ||
    shipment?.shipping_carrier;
  const carrier = [carrierName, label.service].filter(Boolean).join(' · ');
  const labelCost =
    label.rate != null && label.rate > 0
      ? CommonUtility.currencyFormat(label.rate, 'USD', {}, 2)
      : undefined;
  const providerLogo = getProviderLogo(provider?.integration_type);
  const providerAddress = formatAddress(provider?.address);
  const packageDetails = [dimensions, weight].filter(Boolean).join(' · ');
  const carrierDetails = [
    labelCost,
    eta
      ? `Delivery ${DateUtility.formatDate(eta, DateFormat.date)}`
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const refund = async () => {
    setIsRefunding(true);
    try {
      await onRefundLabel?.(label);
      setIsConfirmingRefund(false);
    } catch {
      return;
    } finally {
      setIsRefunding(false);
    }
  };

  return (
    <Card
      title={
        isReturn
          ? count > 1
            ? `Return label ${index + 1} of ${count}`
            : 'Return label'
          : shipmentTitle(index, count)
      }
      action={isReturn ? <Badge tone="info">Return</Badge> : undefined}
      bodyClassName="sk:flex sk:flex-col sk:gap-4"
    >
      <ShipmentItems contents={contents} />

      {contents.length > 0 ? (
        <div className="sk:border-t sk:border-rule" />
      ) : null}

      <FieldGrid className="sk:gap-4 sk:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        <ShipmentFact
          label="Fulfillment"
          visual={
            <img
              src={providerLogo}
              alt=""
              className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-secondary sk:object-contain sk:p-1.5"
            />
          }
          value={label.shipFrom?.name || provider?.name || 'Unknown provider'}
          detail={providerAddress}
        />

        <ShipmentFact
          label="Package"
          visual={
            <span className="sk:flex sk:size-8 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-option sk:bg-fill-secondary sk:text-fg-secondary">
              <PackageIcon size={16} weight="duotone" />
            </span>
          }
          value={label.package || 'Package'}
          detail={packageDetails}
        />

        <ShipmentFact
          label="Carrier"
          visual={
            carrierService?.icon ? (
              <img
                src={carrierService.icon}
                alt=""
                className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-secondary sk:object-contain sk:p-1.5"
              />
            ) : (
              <span className="sk:flex sk:size-8 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-option sk:bg-fill-secondary sk:text-fg-secondary">
                <TruckIcon size={16} weight="duotone" />
              </span>
            )
          }
          value={carrier || 'Unknown carrier'}
          detail={carrierDetails}
        />
      </FieldGrid>

      <div className="sk:flex sk:flex-wrap sk:gap-2 sk:border-t sk:border-rule sk:pt-4">
        <div className="sk:grid sk:min-w-0 sk:flex-1 sk:gap-2 sk:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
          <Button
            variant="primary"
            size="lg"
            isDisabled={!label.url}
            className="sk:w-full sk:rounded-option"
            onPress={() => onViewLabel(label)}
          >
            <EyeIcon size={14} />
            View Label
          </Button>

          <TrackingRow
            trackingNumber={label.tracking_id}
            trackingLink={trackingLink}
          />
        </div>

        {onRefundLabel ? (
          <Button
            variant="danger"
            size="lg"
            className="sk:shrink-0 sk:rounded-option"
            onPress={() => setIsConfirmingRefund(true)}
          >
            Refund
          </Button>
        ) : null}
      </div>

      <Modal
        isOpen={isConfirmingRefund}
        onOpenChange={setIsConfirmingRefund}
        isDismissable={!isRefunding}
        title="Refund this label?"
        footer={
          <>
            <Button
              variant="ghost"
              isDisabled={isRefunding}
              onPress={() => setIsConfirmingRefund(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" isDisabled={isRefunding} onPress={refund}>
              {isRefunding ? 'Refunding…' : 'Refund'}
            </Button>
          </>
        }
      >
        <p className="sk:m-0 sk:text-fg-secondary">
          The label will be voided with {carrier || 'the carrier'} and can no
          longer be used to ship this order.
        </p>
      </Modal>
    </Card>
  );
}

/** No label of ours behind it: facts and tracking only, nothing to act on. */
function ExternalShipmentSummary({
  shipment,
  index,
  count,
}: {
  shipment: ExternalShipment;
  index: number;
  count: number;
}) {
  const carrierService = shipment.carrier
    ? carrierServices[shipment.carrier.toLowerCase()]
    : undefined;
  const carrierName = carrierService?.text || shipment.carrier?.toUpperCase();
  /* EN-4544: V2 reports when the warehouse shipped it; the legacy page is left as it was. */
  const { isV2 } = useOrdersV2();
  const shipped =
    isV2 && shipment.shippedAt
      ? `Shipped ${DateUtility.formatDate(shipment.shippedAt, DateFormat.date)}`
      : undefined;

  return (
    <Card
      title={shipmentTitle(index, count)}
      action={<Badge tone="info">Fulfilled by warehouse</Badge>}
      bodyClassName="sk:flex sk:flex-col sk:gap-4"
    >
      <ShipmentItems contents={shipment.contents} />

      {shipment.contents.length > 0 ? (
        <div className="sk:border-t sk:border-rule" />
      ) : null}

      <FieldGrid className="sk:gap-4 sk:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        <ShipmentFact
          label="Fulfillment"
          visual={
            <img
              src={shipment.providerLogo}
              alt=""
              className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-secondary sk:object-contain sk:p-1.5"
            />
          }
          value={shipment.providerName || 'Unknown provider'}
          detail={shipment.providerAddress}
        />

        <ShipmentFact
          label="Carrier"
          visual={
            carrierService?.icon ? (
              <img
                src={carrierService.icon}
                alt=""
                className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-secondary sk:object-contain sk:p-1.5"
              />
            ) : (
              <span className="sk:flex sk:size-8 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-option sk:bg-fill-secondary sk:text-fg-secondary">
                <TruckIcon size={16} weight="duotone" />
              </span>
            )
          }
          value={carrierName || 'Unknown carrier'}
          detail={
            [
              shipped,
              shipment.eta
                ? `Delivery ${DateUtility.formatDate(shipment.eta, DateFormat.date)}`
                : undefined,
            ]
              .filter(Boolean)
              .join(' · ') || undefined
          }
        />
      </FieldGrid>

      <div className="sk:border-t sk:border-rule sk:pt-4">
        <TrackingRow
          trackingNumber={shipment.trackingNumber}
          trackingLink={shipment.trackingLink}
        />
      </div>
    </Card>
  );
}

/** The shipments the order has already made, whoever's label carried them. */
export function countShippedShipments(
  order: ShipmentSummaryOrder | undefined,
  labels: OrderShippingLabel[] = [],
) {
  const activeLabels = labels.filter(label => !label.refunded);

  return activeLabels.length + externalShipments(order, labels).length;
}

export function ShipmentSummaryList({
  order,
  labels,
  returnLabels = [],
  isLoading,
  isProcessing,
  trackingStatuses = [],
  numbering,
  onViewLabel,
  onRefundLabel,
}: ShipmentSummaryListProps) {
  const activeLabels = labels.filter(label => !label.refunded);
  const activeReturnLabels = returnLabels.filter(label => !label.refunded);
  const external = externalShipments(order, labels);
  const shipped = activeLabels.length + external.length;
  const offset = numbering?.offset || 0;
  const total = numbering?.total ?? shipped;

  if (isLoading && shipped === 0 && activeReturnLabels.length === 0) {
    return (
      <Card title="Shipping">
        <span
          role="status"
          aria-label="Loading shipping details"
          className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary"
        >
          <CircleNotchIcon size={14} className="sk:animate-spin" />
          Loading shipping details
        </span>
      </Card>
    );
  }

  if (shipped === 0 && activeReturnLabels.length === 0) {
    /* Whatever comes after this list speaks for the order instead. */
    if (numbering) {
      return null;
    }

    return (
      <Card title="Shipping">
        {isProcessing ? (
          <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
            <CircleNotchIcon size={14} className="sk:animate-spin" />
            Creating shipment, this can take a moment
          </span>
        ) : (
          <span className="sk:text-dense sk:text-fg-tertiary">
            No shipping details available
          </span>
        )}
      </Card>
    );
  }

  return (
    <div className="sk:flex sk:flex-col sk:gap-6">
      {activeLabels.map((label, index) => (
        <ShipmentSummary
          key={label._id || label.labelId || `${label.tracking_id}-${index}`}
          label={label}
          index={offset + index}
          count={total}
          order={order}
          trackingStatuses={trackingStatuses}
          onViewLabel={onViewLabel}
          onRefundLabel={onRefundLabel}
        />
      ))}

      {external.map((shipment, index) => (
        <ExternalShipmentSummary
          key={shipment.trackingNumber}
          shipment={shipment}
          index={offset + activeLabels.length + index}
          count={total}
        />
      ))}

      {activeReturnLabels.map((label, index) => (
        <ShipmentSummary
          key={
            label._id || label.labelId || `return-${label.tracking_id}-${index}`
          }
          label={label}
          index={index}
          count={activeReturnLabels.length}
          order={order}
          trackingStatuses={trackingStatuses}
          onViewLabel={onViewLabel}
          onRefundLabel={onRefundLabel}
          isReturn
        />
      ))}
    </div>
  );
}
